import { useEffect, useRef, useState, type ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, Calendar, MapPin, Play, Square, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

type CardKey = "pricing" | "hours" | "booking" | "faq";

interface SpotlightCardData {
  key: CardKey;
  genieIcon: GenieIconName;
  title: string;
  triggerHint: string;
  body: ReactNode;
  cta?: { label: string; href: string };
}

const PlanRow = ({
  name,
  price,
  detail,
  highlight,
}: {
  name: string;
  price: string;
  detail: string;
  highlight?: boolean;
}) => (
  <div
    className={cn(
      "flex items-center justify-between rounded-xl px-3 py-2.5 border",
      highlight
        ? "bg-gradient-to-r from-[#4E9CFF]/10 to-[#FF4DB8]/10 border-[#4E9CFF]/30"
        : "bg-gray-50 border-gray-200",
    )}
  >
    <div className="min-w-0">
      <div className="text-sm font-semibold text-[#222B45] flex items-center gap-2">
        {name}
        {highlight && (
          <span className="text-[10px] uppercase tracking-wide text-[#FF4DB8] font-bold">
            Popular
          </span>
        )}
      </div>
      <div className="text-xs text-gray-500">{detail}</div>
    </div>
    <div className="text-sm font-bold text-[#4E9CFF] ml-3 flex-shrink-0">{price}</div>
  </div>
);

const HourRow = ({ day, time }: { day: string; time: string }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-gray-700">{day}</span>
    <span className="text-[#222B45] font-medium">{time}</span>
  </div>
);

const FaqItem = ({ q, a }: { q: string; a: string }) => (
  <div>
    <div className="text-sm font-semibold text-[#222B45]">{q}</div>
    <div className="text-sm text-gray-600 mt-1 leading-relaxed">{a}</div>
  </div>
);

const CARDS: Record<CardKey, SpotlightCardData> = {
  pricing: {
    key: "pricing",
    genieIcon: "clipboard-check",
    title: "Our Plans",
    triggerHint: "when the caller asks about pricing, plans, or how much it costs",
    body: (
      <div className="space-y-2">
        <PlanRow name="Starter" price="$19/mo" detail="1 Genie · 100 conversations" />
        <PlanRow name="Pro" price="$49/mo" detail="5 Genies · 1,000 conversations" highlight />
        <PlanRow name="Business" price="$99/mo" detail="Unlimited Genies & conversations" />
      </div>
    ),
    cta: { label: "View pricing details", href: "#" },
  },
  hours: {
    key: "hours",
    genieIcon: "calendar-check",
    title: "When We're Open",
    triggerHint: "when the caller asks about opening hours, location, or whether we're open right now",
    body: (
      <>
        <div className="space-y-1.5">
          <HourRow day="Monday – Friday" time="9am – 6pm" />
          <HourRow day="Saturday" time="10am – 4pm" />
          <HourRow day="Sunday" time="Closed" />
        </div>
        <div className="mt-3 flex items-center gap-1.5 text-xs text-gray-500">
          <MapPin size={13} className="text-[#FF4DB8]" />
          123 George St, Sydney NSW 2000
        </div>
      </>
    ),
    cta: { label: "Get directions", href: "#" },
  },
  booking: {
    key: "booking",
    genieIcon: "chat-check",
    title: "Book a Free 30-min Demo",
    triggerHint: "when the caller wants to book a demo, schedule a call, or speak with a human",
    body: (
      <>
        <p className="text-sm text-gray-700 leading-relaxed">
          See Help Genie in action with a tailored walkthrough for your business.
          One of our team will show you exactly how a Genie would handle your calls.
        </p>
        <div className="mt-3 rounded-xl bg-gradient-to-br from-[#4E9CFF]/10 to-[#FF4DB8]/10 border border-[#E5EAF0] p-3 flex items-start gap-2 text-xs text-gray-700">
          <Calendar size={14} className="text-[#4E9CFF] mt-0.5 flex-shrink-0" />
          <span>
            <span className="font-semibold text-[#222B45]">Available this week:</span> Mon 2pm, Tue 10am, Thu 4pm
          </span>
        </div>
      </>
    ),
    cta: { label: "Book your demo", href: "#" },
  },
  faq: {
    key: "faq",
    genieIcon: "lightbulb",
    title: "Common Questions",
    triggerHint: "when the caller has a general question about how Help Genie works",
    body: (
      <div className="space-y-4">
        <FaqItem
          q="How long does it take to set up a Genie?"
          a="About 5 minutes from sign-up to a live Genie ready to answer calls."
        />
        <FaqItem
          q="Do I need any technical skills?"
          a="No code, no setup wizards — just describe your business in plain English."
        />
        <FaqItem
          q="What happens after the conversation?"
          a="You get a full transcript, smart summary, and any captured details delivered straight to your inbox."
        />
      </div>
    ),
  },
};

const SpotlightCardView = ({
  card,
  onClose,
}: {
  card: SpotlightCardData;
  onClose: () => void;
}) => (
  <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-[0_20px_60px_-15px_rgba(78,156,255,0.45)]">
    <div className="rounded-[14px] bg-white overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5EAF0]">
        <div className="flex items-center gap-3 min-w-0">
          <GenieIcon name={card.genieIcon} size={28} className="h-7 w-7 flex-shrink-0" />
          <h3 className="text-base font-semibold text-[#222B45] truncate">{card.title}</h3>
        </div>
        <button
          onClick={onClose}
          aria-label="Dismiss spotlight"
          className="text-gray-400 hover:text-gray-600 transition-colors p-1 -mr-1"
        >
          <X size={18} />
        </button>
      </div>

      <div className="px-5 py-5">{card.body}</div>

      {card.cta && (
        <div className="px-5 pb-5">
          <a
            href={card.cta.href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex w-full items-center justify-center px-5 py-3 rounded-xl text-sm font-semibold text-white shadow-lg transition-all hover:scale-[1.02]"
            style={{
              background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
              boxShadow: "0 10px 24px -8px rgba(78,156,255,0.5)",
            }}
          >
            {card.cta.label}
          </a>
        </div>
      )}

      <div className="px-5 py-3 bg-[#F9FAFC] border-t border-[#E5EAF0]">
        <p className="text-[11px] leading-relaxed text-[#64748B]">
          <span className="font-semibold text-[#222B45]">Genie shows this </span>
          {card.triggerHint}.
        </p>
      </div>
    </div>
  </div>
);

const DemoOrb = ({ active }: { active: boolean }) => (
  <motion.div
    className="relative flex items-center justify-center"
    style={{ width: "min(320px, 70vw)", height: "min(320px, 70vw)" }}
    animate={{ scale: active ? [1, 1.04, 1] : [1, 1.015, 1] }}
    transition={{
      duration: active ? 1.5 : 3,
      repeat: Infinity,
      ease: "easeInOut",
    }}
  >
    <div
      className="absolute inset-0 rounded-full"
      style={{
        background: "radial-gradient(circle at 30% 30%, #4E9CFF 0%, #FF4DB8 80%)",
        boxShadow: active
          ? "0 0 80px rgba(78,156,255,0.5), 0 0 120px rgba(255,77,184,0.4)"
          : "0 0 40px rgba(78,156,255,0.3), 0 0 80px rgba(255,77,184,0.25)",
        transition: "box-shadow 400ms ease",
      }}
    />
    <div className="absolute inset-[10%] rounded-full bg-white/20 backdrop-blur-sm" />
    <img
      src="/helpgenie_logo.png"
      alt=""
      className="relative w-[40%] h-[40%] object-contain drop-shadow-lg"
      draggable={false}
    />
  </motion.div>
);

const DemoControls = ({
  activeCard,
  onShow,
  isRunning,
  onToggleScript,
}: {
  activeCard: CardKey | null;
  onShow: (key: CardKey | null) => void;
  isRunning: boolean;
  onToggleScript: () => void;
}) => {
  const cardKeys: CardKey[] = ["pricing", "hours", "booking", "faq"];
  return (
    <div className="rounded-2xl bg-white px-5 py-4 shadow-[0_20px_50px_-15px_rgba(15,23,42,0.18)] border border-[#E5EAF0] max-w-[min(900px,calc(100vw-2rem))]">
      <div className="flex items-center gap-2 mb-3">
        <div className="h-2 w-2 rounded-full bg-[#FF4DB8] animate-pulse" />
        <span className="text-xs font-semibold text-[#222B45]">Demo controls</span>
        <span className="text-xs text-[#64748B] hidden sm:inline">
          — these buttons simulate the Genie calling{" "}
          <code className="px-1.5 py-0.5 rounded bg-gray-100 text-[10px] text-[#222B45] font-mono">
            show_spotlight(key)
          </code>{" "}
          mid-conversation
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        {cardKeys.map((k) => {
          const active = activeCard === k;
          return (
            <button
              key={k}
              onClick={() => onShow(k)}
              className={cn(
                "inline-flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold transition-all",
                active
                  ? "text-white shadow-md scale-[1.02]"
                  : "text-gray-700 bg-gray-100 hover:bg-gray-200",
              )}
              style={
                active
                  ? {
                      background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
                      boxShadow: "0 6px 16px -6px rgba(78,156,255,0.5)",
                    }
                  : undefined
              }
            >
              <GenieIcon name={CARDS[k].genieIcon} size={16} className="h-4 w-4" />
              {CARDS[k].title}
            </button>
          );
        })}
        <button
          onClick={() => onShow(null)}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors"
        >
          <X size={13} /> Dismiss
        </button>
        <div className="w-px h-7 bg-[#E5EAF0] mx-1" />
        <button
          onClick={onToggleScript}
          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-white shadow-md transition-all hover:scale-105"
          style={{
            background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)",
            boxShadow: "0 6px 16px -6px rgba(78,156,255,0.5)",
          }}
        >
          {isRunning ? (
            <>
              <Square size={13} /> Stop demo
            </>
          ) : (
            <>
              <Play size={13} /> Run scripted demo
            </>
          )}
        </button>
      </div>
    </div>
  );
};

const SpotlightDemo = () => {
  const navigate = useNavigate();
  const [activeCard, setActiveCard] = useState<CardKey | null>(null);
  const [isRunningScript, setIsRunningScript] = useState(false);
  const scriptTimersRef = useRef<number[]>([]);

  const clearScript = () => {
    scriptTimersRef.current.forEach((t) => window.clearTimeout(t));
    scriptTimersRef.current = [];
    setIsRunningScript(false);
  };

  const showCard = (key: CardKey | null) => {
    clearScript();
    setActiveCard(key);
  };

  const runScript = () => {
    clearScript();
    setIsRunningScript(true);
    setActiveCard(null);
    const sequence: Array<{ at: number; card: CardKey | null }> = [
      { at: 500, card: "pricing" },
      { at: 4500, card: "hours" },
      { at: 8500, card: "booking" },
      { at: 12500, card: "faq" },
      { at: 17000, card: null },
    ];
    sequence.forEach(({ at, card }) => {
      const t = window.setTimeout(() => setActiveCard(card), at);
      scriptTimersRef.current.push(t);
    });
    const stopT = window.setTimeout(() => setIsRunningScript(false), 17500);
    scriptTimersRef.current.push(stopT);
  };

  useEffect(() => () => clearScript(), []);

  const statusText = activeCard
    ? `Showing — ${CARDS[activeCard].title}`
    : isRunningScript
      ? "Speaking…"
      : "Speaking — try the controls below";

  return (
    <div className="relative min-h-dvh w-full overflow-hidden bg-[#F9FAFC]">
      <Helmet>
        <title>Spotlight Panel · Internal Demo</title>
      </Helmet>

      {/* Top bar */}
      <div className="absolute inset-x-0 top-0 z-30 flex items-center justify-between px-6 py-4">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1.5 rounded-full bg-white/70 backdrop-blur px-3 py-1.5 text-xs font-medium text-[#64748B] hover:text-[#222B45] hover:bg-white transition-colors border border-[#E5EAF0]"
          aria-label="Back"
        >
          <ArrowLeft size={14} /> Back
        </button>
        <div className="rounded-full bg-white px-3.5 py-1.5 text-[11px] font-semibold text-[#64748B] shadow-sm border border-[#E5EAF0] uppercase tracking-wider">
          Internal demo · Spotlight Panel
        </div>
        <div className="w-[72px]" />
      </div>

      {/* Spotlight Panel — anchored to the left */}
      <div className="absolute left-0 top-0 bottom-0 z-10 flex items-center pointer-events-none">
        <div className="pl-4 md:pl-10 pointer-events-auto">
          <AnimatePresence mode="wait">
            {activeCard && (
              <motion.div
                key={activeCard}
                initial={{ x: -60, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                exit={{ x: -60, opacity: 0 }}
                transition={{ duration: 0.32, ease: [0.22, 1, 0.36, 1] }}
                className="w-[380px] max-w-[calc(100vw-2rem)]"
              >
                <SpotlightCardView card={CARDS[activeCard]} onClose={() => showCard(null)} />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Avatar + status — center */}
      <div className="relative z-0 flex min-h-dvh flex-col items-center justify-center px-4">
        <p className="mb-8 text-center text-[1rem] md:text-[1.05rem] font-medium text-[#666] max-w-[320px]">
          {statusText}
        </p>

        <DemoOrb active={!!activeCard || isRunningScript} />

        <button className="mt-8 rounded-3xl border-2 border-[#4E9CFF] bg-[#4E9CFF]/10 px-6 py-2 text-sm font-semibold text-[#4E9CFF] hover:bg-[#4E9CFF]/15 transition-colors">
          End Conversation
        </button>
      </div>

      {/* Demo controls — bottom */}
      <div className="absolute inset-x-0 bottom-6 z-20 flex justify-center px-4">
        <DemoControls
          activeCard={activeCard}
          onShow={showCard}
          isRunning={isRunningScript}
          onToggleScript={isRunningScript ? clearScript : runScript}
        />
      </div>
    </div>
  );
};

export default SpotlightDemo;
